/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Jobs Hooks - React Query for Scheduled Jobs Dashboard
 * ============================================================================
 *
 * Custom hooks for scheduled jobs data fetching using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  jobsService,
  ScheduledJob,
  JobDashboardOverview,
  JobExecution,
} from '@/services/superadmin/jobs.service';
import { ApiError, PagedResponse } from '@/services/api/types';

// Query keys for cache management
export const jobsKeys = {
  all: ['jobs'] as const,
  list: () => [...jobsKeys.all, 'list'] as const,
  overview: () => [...jobsKeys.all, 'overview'] as const,
  detail: (name: string) => [...jobsKeys.all, 'detail', name] as const,
  history: (name: string, page: number, size: number) =>
    [...jobsKeys.all, 'history', name, { page, size }] as const,
  executions: (page: number, size: number) =>
    [...jobsKeys.all, 'executions', { page, size }] as const,
};

/**
 * Hook to fetch all scheduled jobs
 */
export function useJobs() {
  return useQuery<ScheduledJob[], ApiError>({
    queryKey: jobsKeys.list(),
    queryFn: jobsService.getAllJobs,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook to fetch dashboard overview
 */
export function useJobDashboardOverview() {
  return useQuery<JobDashboardOverview, ApiError>({
    queryKey: jobsKeys.overview(),
    queryFn: jobsService.getDashboardOverview,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Hook to fetch a single job
 */
export function useJob(name: string) {
  return useQuery<ScheduledJob, ApiError>({
    queryKey: jobsKeys.detail(name),
    queryFn: () => jobsService.getJob(name),
    enabled: !!name,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch job execution history
 */
export function useJobHistory(name: string, page: number = 0, size: number = 20) {
  return useQuery<PagedResponse<JobExecution>, ApiError>({
    queryKey: jobsKeys.history(name, page, size),
    queryFn: () => jobsService.getJobHistory(name, page, size),
    enabled: !!name,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch recent executions
 */
export function useRecentExecutions(page: number = 0, size: number = 20) {
  return useQuery<PagedResponse<JobExecution>, ApiError>({
    queryKey: jobsKeys.executions(page, size),
    queryFn: () => jobsService.getRecentExecutions(page, size),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to trigger a job
 */
export function useTriggerJob() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: jobsService.triggerJob,
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: jobsKeys.all });
    },
  });
}

/**
 * Hook to pause a job
 */
export function usePauseJob() {
  const queryClient = useQueryClient();

  return useMutation<ScheduledJob, ApiError, string>({
    mutationFn: jobsService.pauseJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsKeys.all });
    },
  });
}

/**
 * Hook to resume a job
 */
export function useResumeJob() {
  const queryClient = useQueryClient();

  return useMutation<ScheduledJob, ApiError, string>({
    mutationFn: jobsService.resumeJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsKeys.all });
    },
  });
}
